import { useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { clearUsageStatsError } from '@/redux/slices/usageStatsSlice'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  downloadUsageStatsExport,
  fetchUsageStatsAggregate,
} from '@/redux/asyncThunks/usageStats'
import { toast } from 'sonner'
import { formatUsageStatsDate } from '@/utils/dateTimeUtils'

const TableRowSkeleton = () => (
  <tr className='border-t animate-pulse'>
    {[1, 2, 3, 4, 5].map((i) => (
      <td key={i} className='px-3 py-2'>
        <span className='inline-block h-4 w-16 bg-slate-200 rounded' />
      </td>
    ))}
  </tr>
)

const UsageStatsPanel = () => {
  const { roomId = '' } = useParams()
  const dispatch = useAppDispatch()
  const {
    aggregate,
    isLoadingAggregate,
    isExporting,
    error,
  } = useAppSelector((state) => state.usageStats)

  const loadAggregate = useCallback(() => {
    if (!roomId) return
    dispatch(fetchUsageStatsAggregate(roomId))
  }, [dispatch, roomId])

  useEffect(() => {
    loadAggregate()
  }, [loadAggregate])

  useEffect(() => {
    if (error) {
      toast.error(error)
      dispatch(clearUsageStatsError())
    }
  }, [dispatch, error])

  const playerRows = aggregate?.players ?? []

  const downloadBlob = (
    blob: Blob,
    filename: string
  ) => {
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  }

  const handleExportAggregate = async () => {
    if (!roomId) return
    try {
      const blob = await dispatch(downloadUsageStatsExport(roomId)).unwrap()
      downloadBlob(blob, `usage-stats-aggregate.csv`)
      toast.success('Downloaded aggregate usage stats as CSV')
    } catch (exportError) {
      toast.error(
        exportError instanceof Error ? exportError.message : 'Export failed'
      )
    }
  }

  return (
    <Card className='p-4 space-y-4'>
      <div className='flex items-center justify-between'>
        <h3 className='text-lg font-semibold text-dark-blue'>Usage Stats & Summaries</h3>
        <div className='flex gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={handleExportAggregate}
            disabled={isExporting || !aggregate}
          >
            Export CSV
          </Button>
        </div>
      </div>
      <div className='grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-3 rounded-lg bg-slate-50 border border-slate-200 px-5 py-4'>
        <div className='font-bold text-slate-800'>
          Total Game Time:{' '}
          <span className='font-semibold text-dark-blue block mt-0.5'>
            {aggregate != null
              ? `${aggregate.metrics.time_spent_minutes_total} minutes`
              : '-'}
          </span>
        </div>
        <div className='font-bold text-slate-800'>
          Total Rounds:{' '}
          <span className='font-semibold text-dark-blue block mt-0.5'>
            {aggregate?.metrics.total_rounds ?? '-'}
          </span>
        </div>
        <div className='font-bold text-slate-800'>
          Total Teams:{' '}
          <span className='font-semibold text-dark-blue block mt-0.5'>
            {aggregate?.metrics.total_teams ?? '-'}
          </span>
        </div>
        <div className='font-bold text-slate-800'>
          Total Players:{' '}
          <span className='font-semibold text-dark-blue block mt-0.5'>
            {aggregate?.metrics.total_players ?? '-'}
          </span>
        </div>
      </div>

      <div className='rounded border'>
        <table className='w-full text-sm'>
          <thead className='bg-gray-50 text-left'>
            <tr>
              <th className='px-3 py-2'>Player</th>
              <th className='px-3 py-2'>Advisor Messages</th>
              <th className='px-3 py-2'>Negotiation Messages</th>
              <th className='px-3 py-2'>Decisions</th>
              <th className='px-3 py-2'>Last Active</th>
            </tr>
          </thead>
          <tbody>
            {isLoadingAggregate &&
              Array.from({ length: 5 }).map((_, i) => (
                <TableRowSkeleton key={i} />
              ))}
            {!isLoadingAggregate &&
              playerRows.map((player) => (
                <tr key={player.room_member_id} className='border-t'>
                  <td className='px-3 py-2'>{player.name}</td>
                  <td className='px-3 py-2'>{player.advisor_messages}</td>
                  <td className='px-3 py-2'>{player.negotiation_messages}</td>
                  <td className='px-3 py-2'>{player.decisions_made}</td>
                  <td className='px-3 py-2'>{formatUsageStatsDate(player.last_active_at)}</td>
                </tr>
              ))}
            {!isLoadingAggregate && !playerRows.length && (
              <tr>
                <td className='px-3 py-3 text-gray-500' colSpan={5}>
                  No player usage data found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

export default UsageStatsPanel
